import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiError } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

function AmountModal({ open, onOpenChange, title, desc, endpoint, label, onDone, testid, confirmLabel }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Sahi amount daalein");
    setLoading(true);
    try {
      await api.post(endpoint, { amount: amt, note });
      toast.success("Ho gaya");
      setAmount(""); setNote("");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>{label} (₹)</Label>
            <Input type="number" data-testid={`${testid}-amount`} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea data-testid={`${testid}-note`} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid={`${testid}-submit`} onClick={submit} disabled={loading}>
            {loading ? "…" : confirmLabel || "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminWithdrawModal({ open, onOpenChange, target, onDone }) {
  return (
    <AmountModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Admin Withdraw — ${target?.name || ""}`}
      desc={`Member ke balance se paisa debit karein. Balance: ₹${target?.balance ?? 0}`}
      endpoint={`/admin/members/${target?.id}/withdraw`}
      label="Amount"
      testid="admin-withdraw-modal"
      confirmLabel="Debit"
      onDone={onDone}
    />
  );
}

export function OldFundModal({ open, onOpenChange, target, onDone }) {
  return (
    <AmountModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Old Fund Entry — ${target?.name || ""}`}
      desc="Purana jama / opening balance record karein (savings me add hoga)."
      endpoint={`/admin/members/${target?.id}/old-fund`}
      label="Fund Amount"
      testid="old-fund-modal"
      confirmLabel="Add Fund"
      onDone={onDone}
    />
  );
}

export function OldLoanModal({ open, onOpenChange, target, onDone }) {
  return (
    <AmountModal
      open={open}
      onOpenChange={onOpenChange}
      title={`Old Loan Entry — ${target?.name || ""}`}
      desc="Purana loan record karein (loan taken + remaining me add hoga)."
      endpoint={`/admin/members/${target?.id}/old-loan`}
      label="Loan Amount"
      testid="old-loan-modal"
      confirmLabel="Add Loan"
      onDone={onDone}
    />
  );
}

export function SetCreditModal({ open, onOpenChange, target, onDone }) {
  const [score, setScore] = useState(target?.credit_score || 3);
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/members/${target.id}/set-credit`, { credit_score: Number(score) });
      toast.success("Credit score update ho gaya");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-credit-modal">
        <DialogHeader>
          <DialogTitle>Set Credit Score — {target?.name}</DialogTitle>
          <DialogDescription>1 se 5 star rating set karein.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 py-4 justify-center">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              data-testid={`credit-star-${n}`}
              onClick={() => setScore(n)}
              className={`text-4xl transition-transform hover:scale-110 ${n <= score ? "text-amber-400" : "text-muted"}`}
            >
              ★
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="set-credit-save-btn" onClick={save} disabled={loading}>
            {loading ? "…" : `Set ${score}★`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminEditModal({ open, onOpenChange, target, onDone }) {
  const [form, setForm] = useState({});
  React.useEffect(() => {
    if (target)
      setForm({
        name: target.name,
        mobile: target.mobile,
        address: target.address || "",
        dob: target.dob || "",
        balance: target.balance,
        savings: target.savings,
        kyc_status: target.kyc_status,
      });
  }, [target]);
  const [loading, setLoading] = useState(false);
  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/members/${target.id}`, {
        ...form,
        balance: Number(form.balance),
        savings: Number(form.savings),
      });
      toast.success("Member update ho gaya");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-edit-modal">
        <DialogHeader>
          <DialogTitle>Edit Member — {target?.name}</DialogTitle>
          <DialogDescription>Admin poori detail badal sakta hai.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-2 col-span-2">
            <Label>Naam</Label>
            <Input data-testid="admin-edit-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Mobile</Label>
            <Input data-testid="admin-edit-mobile" value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>DOB</Label>
            <Input type="date" value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Balance ₹</Label>
            <Input type="number" data-testid="admin-edit-balance" value={form.balance ?? ""} onChange={(e) => setForm({ ...form, balance: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Savings ₹</Label>
            <Input type="number" value={form.savings ?? ""} onChange={(e) => setForm({ ...form, savings: e.target.value })} />
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Address</Label>
            <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="admin-edit-save-btn" onClick={save} disabled={loading}>
            {loading ? "…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddMemberModal({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ name: "", mobile: "", balance: "", savings: "" });
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!form.name || !form.mobile) return toast.error("Naam aur mobile zaroori hai");
    setLoading(true);
    try {
      await api.post("/admin/members", {
        name: form.name,
        mobile: form.mobile,
        balance: Number(form.balance || 0),
        savings: Number(form.savings || 0),
      });
      toast.success("Naya member add ho gaya");
      setForm({ name: "", mobile: "", balance: "", savings: "" });
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-member-modal">
        <DialogHeader>
          <DialogTitle>Naya Member Add Karein</DialogTitle>
          <DialogDescription>Login OTP sabhi ke liye 1234 hai.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label>Naam</Label>
            <Input data-testid="add-member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Mobile</Label>
            <Input data-testid="add-member-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Balance ₹</Label>
              <Input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Savings ₹</Label>
              <Input type="number" value={form.savings} onChange={(e) => setForm({ ...form, savings: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="add-member-save-btn" onClick={save} disabled={loading}>
            {loading ? "…" : "Add Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkImportModal({ open, onOpenChange, onDone }) {
  const [text, setText] = useState("Name, Mobile, Balance, Savings\nNeha Gupta, 9700011122, 5000, 12000");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const lines = text.trim().split("\n");
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const [name, mobile, balance, savings] = lines[i].split(",").map((x) => x.trim());
        if (name && mobile) rows.push({ name, mobile, balance, savings });
      }
      const { data } = await api.post("/admin/bulk-import", { rows });
      toast.success(`${data.added} members import hue`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-import-modal">
        <DialogHeader>
          <DialogTitle>Bulk Import (CSV)</DialogTitle>
          <DialogDescription>
            Pehli line header hai. Format: Name, Mobile, Balance, Savings
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="bulk-import-textarea"
          rows={8}
          className="font-mono text-xs"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="bulk-import-submit-btn" onClick={submit} disabled={loading}>
            {loading ? "Import…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
